import React, { useCallback, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { Alert } from '@mui/material';

import OrderSearchForm, { MultiFilterState } from '~/components/Common/Forms/OrderSearchForm';
import Spinner from '~/components/Common/Spinner';
import FraudWarning from '~/components/Users/History/FraudWarning';
import { withTenant } from '~/components/hoc';

import { ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import { getPurchases, multiSearch, searchPurchases } from '~/services/OrdersService';

import { addQuery, removeQuery } from '~/utils/url';

import OrdersPage from './OrdersPage';

const DEFAULT_SIZE_PER_PAGE = 10;
const SEARCH_BY_CUSTOMER_ID_SIZE_PER_PAGE = 50;

interface Props {
  tenant: App.Tenant;
}

function OrdersPageContainer({ tenant }: Props) {
  const { push } = useAppNavigation();
  const location = useLocation();
  const query = useQuery();
  const { user } = useCurrentUser();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [purchases, setPurchases] = useState(null);
  const [filterField, setFilterField] = useState(null);
  const [filterValue, setFilterValue] = useState(null);
  const [additionalSearchParams, setAdditionalSearchParams] = useState<MultiFilterState>();
  const [businessId, setBusinessId] = useState<string | null>(query.get('business_id'));
  // bumped on every submit so repeating an identical search still refetches
  const [searchNonce, setSearchNonce] = useState(0);

  const [sizePerPage, setSizePerPage] = useState(DEFAULT_SIZE_PER_PAGE);

  const currentPage = parseInt(query.get('page')) || 1;
  const customerId = query.get('customer_id');
  const shouldIncludeSharedOrders = Boolean(customerId);

  const isReady = !isLoading && !error && purchases;

  const searchQuery = (field, value, params?: { businessId?: string | null }) => {
    setFilterField(field);
    setFilterValue(value);
    setBusinessId(params?.businessId ?? null);
    setSearchNonce((nonce) => nonce + 1);

    let newLocation = removeQuery(location, 'customer_id', 'page');
    if (params?.businessId) {
      newLocation = addQuery(newLocation, { business_id: params.businessId });
    } else {
      newLocation = removeQuery(newLocation, 'business_id');
    }

    push(newLocation);
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setPurchases(null);
    setError(null);

    if (user?.roles.includes(ROLE_ICS_STORE_TEAM)) {
      // we don't want these users to be able to see all results so
      // if there are no filters selected, just return empty
      if (!filterField || !filterValue) {
        setPurchases(null);
        setIsLoading(false);
        return;
      }
    }
    try {
      let fetchedPurchases;
      if (filterField === 'universal_search_id') {
        fetchedPurchases = await searchPurchases({
          page: currentPage - 1,
          perPage: sizePerPage,
          searchId: filterValue,
        });
      } else if (filterField === 'multi_search') {
        fetchedPurchases = await multiSearch({
          page: currentPage - 1,
          perPage: sizePerPage,
          orderBy: 'created_at',
          orderDirection: 'desc',
          additionalFilter: additionalSearchParams,
        });
      } else {
        fetchedPurchases = await getPurchases({
          page: currentPage,
          per_page: sizePerPage,
          brand: tenant.brand,
          customer_id: customerId,
          filterBy: filterField,
          filterValue,
          additionalFilter: additionalSearchParams,
          businessId: businessId || undefined,
          includeSharedOrders: shouldIncludeSharedOrders,
        });
      }
      setPurchases(fetchedPurchases);
      setIsLoading(false);
    } catch (error) {
      setError(JSON.stringify(error));
      setIsLoading(false);
    }
  }, [
    user,
    filterField,
    filterValue,
    currentPage,
    sizePerPage,
    additionalSearchParams,
    tenant.brand,
    customerId,
    businessId,
  ]);

  const onPageChange = (page) => {
    const newLocation = addQuery(location, { page });
    push(newLocation);
  };

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [fetchData, user, searchNonce]);

  useEffect(() => {
    if (customerId) {
      setSizePerPage(SEARCH_BY_CUSTOMER_ID_SIZE_PER_PAGE);
    } else {
      setSizePerPage(DEFAULT_SIZE_PER_PAGE);
    }
  }, [customerId]);

  return (
    <>
      <OrderSearchForm
        searchQuery={searchQuery}
        setAdditionalParams={setAdditionalSearchParams}
        bookingNumberPlaceholder="Search Orders or Booking Numbers"
        bookingIdType="universal_search_id"
        e2eSuffix="-universal-search"
        showMultiSearch
        showOrderNotesFilter
        initialBusinessId={businessId || undefined}
      />

      {customerId && <FraudWarning userId={customerId} />}

      {isLoading && <Spinner size={36} />}

      {error && <Alert severity="warning">Failed to load data: {error}</Alert>}

      {isReady && (
        <OrdersPage
          onPageChange={onPageChange}
          page={currentPage}
          purchases={purchases.result}
          sizePerPage={sizePerPage}
          total={purchases?.total}
        />
      )}
    </>
  );
}

export default withTenant(OrdersPageContainer);
